/**
 * Calendar View Component
 * Provides interface for managing campaign calendar settings
 */

import { formatGameDate, formatGameDateWithDays, calculateDaysElapsed, MONTH_NAMES, MONTH_LENGTHS, DEFAULT_GAME_DATE } from '../utils/calendar.js';
import { CONFIG } from '../config.js';
import { HexEvents, createHexEvent } from '../utils/hex-events.js';
import { EventManagerMixin } from '../utils/event-manager.js';
import { showFieldError, clearErrorOnInput } from '../utils/validation.js';
import { toast } from '../utils/toast.js';

class CalendarView extends EventManagerMixin(HTMLElement) {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.session = null;
    this.timelineSearchQuery = '';
    this.timelineFilter = 'all';
  }

  connectedCallback() {
    this.render();
  }

  setSession(session) {
    this.session = session;
    this.render();
  }

  render() {
    if (!this.session) {
      this.shadowRoot.innerHTML = '<div>Loading...</div>';
      return;
    }

    const startDate = this.session.calendar.campaignStartDate;
    const currentDate = this.session.calendar.currentDate;
    const daysElapsed = this.session.calendar.daysElapsed;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          height: 100%;
          background: var(--bg-surface);
          color: var(--text-primary);
          overflow-y: auto;
        }

        .calendar-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
        }

        .calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          border-bottom: 2px solid var(--border-subtle);
          padding-bottom: 1rem;
        }

        h1 {
          margin: 0;
          font-size: 1.8rem;
          color: var(--text-primary);
        }

        .btn {
          padding: 0.5rem 1rem;
          background: var(--color-primary);
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
          transition: background 0.2s;
        }

        .btn:hover {
          background: #2563eb;
        }

        .section {
          background: var(--bg-elevated);
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .section h2 {
          margin-top: 0;
          margin-bottom: 1rem;
          font-size: 1.2rem;
          color: var(--color-primary);
        }

        .date-config {
          display: grid;
          gap: 1.5rem;
        }

        .date-field {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .date-field label {
          font-weight: 600;
          color: var(--text-secondary);
        }

        .date-pickers {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        select, input[type="number"] {
          padding: 0.5rem;
          background: var(--bg-input);
          color: var(--text-primary);
          border: 1px solid var(--border-default);
          border-radius: 4px;
          font-size: 0.9rem;
        }

        select:focus, input[type="number"]:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        .month-select {
          flex: 2;
        }

        .day-select, .year-input {
          flex: 1;
        }

        .info-display {
          padding: 1rem;
          background: var(--bg-input);
          border-radius: 4px;
          border-left: 3px solid var(--color-primary);
        }

        .info-display strong {
          color: var(--color-primary);
        }

        .days-elapsed {
          font-size: 1.1rem;
          color: var(--text-primary);
        }

        .quick-advance {
          display: flex;
          gap: 0.5rem;
          margin-top: 1rem;
        }

        .btn-advance {
          padding: 0.5rem 1rem;
          background: var(--color-success);
          color: var(--bg-surface);
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 600;
          transition: background 0.2s;
        }

        .btn-advance:hover {
          background: #059669;
        }

        .warning {
          padding: 1rem;
          background: #fef3c7;
          color: #92400e;
          border-radius: 4px;
          border-left: 3px solid var(--color-warning);
          margin-top: 1rem;
        }

        .separator {
          color: var(--text-disabled);
        }

        .timeline {
          max-height: 400px;
          overflow-y: auto;
        }

        .timeline-date-group {
          margin-bottom: 1.5rem;
        }

        .timeline-date-header {
          font-weight: 600;
          color: var(--color-primary);
          font-size: 0.95rem;
          margin-bottom: 0.5rem;
          padding-bottom: 0.25rem;
          border-bottom: 1px solid var(--border-subtle);
        }

        .timeline-events {
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .timeline-event {
          padding: 0.5rem 0.75rem;
          margin-bottom: 0.25rem;
          background: var(--bg-input);
          border-radius: 4px;
          font-size: 0.875rem;
          border-left: 3px solid var(--color-success);
        }

        .timeline-event-hex_reveal {
          border-left-color: var(--color-success);
        }

        .timeline-event-hex_conceal {
          border-left-color: var(--color-warning);
        }

        .timeline-event-note {
          border-left-color: var(--color-primary);
        }

        .timeline-event-icon {
          border-left-color: #a78bfa;
        }

        .timeline-event-token_move {
          border-left-color: #f472b6;
        }

        .timeline-event-bookmark {
          border-left-color: #ec4899;
        }

        .timeline-event-journal {
          border-left-color: #14b8a6;
        }

        .bookmark-title {
          font-weight: bold;
        }

        .btn-add-bookmark {
          padding: 0.5rem 1rem;
          background: #ec4899;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 600;
          transition: background 0.2s;
          margin-bottom: 1rem;
        }

        .btn-add-bookmark:hover {
          background: #db2777;
        }

        .btn-add-journal {
          padding: 0.5rem 1rem;
          background: #14b8a6;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 600;
          transition: background 0.2s;
          margin-bottom: 1rem;
          margin-left: 0.5rem;
        }

        .btn-add-journal:hover {
          background: #0d9488;
        }

        .timeline-controls {
          display: flex;
          gap: 0.5rem;
          margin: 1rem 0;
        }

        .timeline-search {
          flex: 1;
          padding: 0.5rem;
          background: var(--bg-input);
          border: 1px solid var(--border-default);
          border-radius: 4px;
          color: var(--text-primary);
          font-size: 0.9rem;
        }

        .timeline-search:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        .timeline-filter {
          padding: 0.5rem;
          background: var(--bg-input);
          border: 1px solid var(--border-default);
          border-radius: 4px;
          color: var(--text-primary);
          font-size: 0.9rem;
          cursor: pointer;
        }

        .timeline-filter:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        .bookmark-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: ${CONFIG.COLORS.OVERLAY_DARKER};
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: ${CONFIG.Z_INDEX_MODAL};
        }

        .bookmark-form {
          background: var(--bg-elevated);
          border-radius: 8px;
          padding: 2rem;
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .bookmark-form h2 {
          margin-top: 0;
          color: #ec4899;
        }

        .form-field {
          margin-bottom: 1rem;
        }

        .form-field label {
          display: block;
          margin-bottom: 0.5rem;
          color: var(--text-secondary);
          font-weight: 600;
        }

        .form-field input[type="text"],
        .form-field textarea {
          width: 100%;
          padding: 0.5rem;
          background: var(--bg-input);
          color: var(--text-primary);
          border: 1px solid var(--border-default);
          border-radius: 4px;
          font-size: 0.9rem;
          font-family: inherit;
          box-sizing: border-box;
        }

        .form-field textarea {
          min-height: 100px;
          resize: vertical;
        }

        .form-field input:focus,
        .form-field textarea:focus {
          outline: none;
          border-color: #ec4899;
        }

        .form-actions {
          display: flex;
          gap: 0.5rem;
          margin-top: 1.5rem;
        }

        .btn-save {
          flex: 1;
          padding: 0.75rem;
          background: var(--color-success);
          color: var(--bg-surface);
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .btn-save:hover {
          background: #059669;
        }

        .btn-cancel {
          flex: 1;
          padding: 0.75rem;
          background: var(--border-default);
          color: var(--text-primary);
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .btn-cancel:hover {
          background: #555;
        }

        .hidden {
          display: none;
        }
      </style>

      <div class="calendar-container">
        <div class="calendar-header">
          <h1>📅 Campaign Calendar</h1>
          <button id="back-to-map-btn" class="btn">
            ← Back to Map
          </button>
        </div>

        <div class="section">
          <h2>Date Configuration</h2>
          <div class="date-config">
            <div class="date-field">
              <label>Campaign Start Date</label>
              <div class="date-pickers">
                <select id="start-month" class="month-select">
                  ${MONTH_NAMES.map((name, i) =>
                    `<option value="${i + 1}" ${startDate.month === i + 1 ? 'selected' : ''}>${name}</option>`
                  ).join('')}
                </select>
                <select id="start-day" class="day-select">
                  ${this.getDayOptions(startDate.month, startDate.day)}
                </select>
                <span class="separator">,</span>
                <input
                  type="number"
                  id="start-year"
                  class="year-input"
                  min="1"
                  value="${startDate.year}"
                  placeholder="Year"
                />
              </div>
            </div>

            <div class="date-field">
              <label>Current Date</label>
              <div class="date-pickers">
                <select id="current-month" class="month-select">
                  ${MONTH_NAMES.map((name, i) =>
                    `<option value="${i + 1}" ${currentDate.month === i + 1 ? 'selected' : ''}>${name}</option>`
                  ).join('')}
                </select>
                <select id="current-day" class="day-select">
                  ${this.getDayOptions(currentDate.month, currentDate.day)}
                </select>
                <span class="separator">,</span>
                <input
                  type="number"
                  id="current-year"
                  class="year-input"
                  min="1"
                  value="${currentDate.year}"
                  placeholder="Year"
                />
              </div>
            </div>

            <div class="info-display">
              <div class="days-elapsed">
                <strong>Days Elapsed:</strong> ${daysElapsed} days
              </div>
            </div>

            <div class="quick-advance">
              <button class="btn-advance" data-days="1">+1 Day</button>
              <button class="btn-advance" data-days="7">+7 Days</button>
              <button class="btn-advance" data-days="30">+30 Days</button>
            </div>
          </div>
        </div>

        <div class="section">
          <h2>Campaign Information</h2>
          <div class="info-display">
            <p><strong>Map:</strong> ${this.session.mapName}</p>
            <p><strong>Start Date:</strong> ${formatGameDate(startDate)}</p>
            <p><strong>Current Date:</strong> ${formatGameDateWithDays(currentDate, daysElapsed)}</p>
          </div>
        </div>

        <div class="section">
          <h2>Campaign Timeline</h2>
          <button id="add-bookmark-btn" class="btn-add-bookmark">
            📌 + Add Bookmark
          </button>
          <button id="add-journal-btn" class="btn-add-journal">
            📝 + Add Journal Entry
          </button>

          <div class="timeline-controls">
            <input
              type="text"
              id="timeline-search"
              class="timeline-search"
              placeholder="Search timeline events..."
              aria-label="Search timeline events"
            />
            <select id="timeline-filter" class="timeline-filter" aria-label="Filter timeline by type">
              <option value="all">All Events</option>
              <option value="bookmarks">Bookmarks Only</option>
              <option value="journal">Journal Only</option>
            </select>
          </div>

          ${this.renderTimeline()}
        </div>
      </div>

      <div id="bookmark-modal" class="bookmark-modal hidden">
        <div class="bookmark-form">
          <h2>📌 Add Campaign Bookmark</h2>
          <div class="form-field">
            <label for="bookmark-title">Title *</label>
            <input type="text" id="bookmark-title" placeholder="e.g., Dragon Discovered" required />
          </div>
          <div class="form-field">
            <label for="bookmark-description">Description</label>
            <textarea id="bookmark-description" placeholder="e.g., Party found ancient dragon lair"></textarea>
          </div>
          <div class="form-field">
            <label>Date *</label>
            <div class="date-pickers">
              <select id="bookmark-month" class="month-select">
                ${MONTH_NAMES.map((name, i) =>
                  `<option value="${i + 1}">${name}</option>`
                ).join('')}
              </select>
              <select id="bookmark-day" class="day-select">
                ${this.getDayOptions(1, 1)}
              </select>
              <span class="separator">,</span>
              <input
                type="number"
                id="bookmark-year"
                class="year-input"
                min="1"
                value="1"
                placeholder="Year"
              />
            </div>
          </div>
          <div class="form-actions">
            <button id="save-bookmark-btn" class="btn-save">Save</button>
            <button id="cancel-bookmark-btn" class="btn-cancel">Cancel</button>
          </div>
        </div>
      </div>

      <div id="journal-modal" class="bookmark-modal hidden">
        <div class="bookmark-form">
          <h2>📝 Add Journal Entry</h2>
          <div class="form-field">
            <label for="journal-description">Entry *</label>
            <textarea id="journal-description" placeholder="e.g., Party rested at inn and gathered rumors" required></textarea>
          </div>
          <div class="form-field">
            <label>Date *</label>
            <div class="date-pickers">
              <select id="journal-month" class="month-select">
                ${MONTH_NAMES.map((name, i) =>
                  `<option value="${i + 1}">${name}</option>`
                ).join('')}
              </select>
              <select id="journal-day" class="day-select">
                ${this.getDayOptions(1, 1)}
              </select>
              <span class="separator">,</span>
              <input
                type="number"
                id="journal-year"
                class="year-input"
                min="1"
                value="1"
                placeholder="Year"
              />
            </div>
          </div>
          <div class="form-actions">
            <button id="save-journal-btn" class="btn-save">Save</button>
            <button id="cancel-journal-btn" class="btn-cancel">Cancel</button>
          </div>
        </div>
      </div>
    `;

    // Set up event listeners after rendering
    this.setupEventListeners();
  }

  getDayOptions(month, selectedDay) {
    const maxDays = MONTH_LENGTHS[month - 1];
    let options = '';
    for (let day = 1; day <= maxDays; day++) {
      options += `<option value="${day}" ${day === selectedDay ? 'selected' : ''}>${day}</option>`;
    }
    return options;
  }

  /**
   * Render the timeline section
   * @returns {string} HTML for the timeline
   */
  renderTimeline() {
    const eventGroups = this.groupEventsByDate();

    if (eventGroups.length === 0) {
      return `<p style="color: ${CONFIG.COLORS.TEXT_MUTED}; font-style: italic;">No events recorded yet. Make changes to the map to see them appear here.</p>`;
    }

    return `
      <div class="timeline">
        ${eventGroups.map(group => `
          <div class="timeline-date-group">
            <div class="timeline-date-header">
              ${group.dateFormatted}
            </div>
            <ul class="timeline-events">
              ${group.events.map(event => `
                <li class="timeline-event timeline-event-${event.type}">
                  ${event.description}
                </li>
              `).join('')}
            </ul>
          </div>
        `).join('')}
      </div>
    `;
  }

  setupEventListeners() {
    const backBtn = this.shadowRoot.getElementById('back-to-map-btn');
    if (backBtn) {
      this.addManagedListener(backBtn, 'click', () => {
        this.dispatchEvent(createHexEvent(HexEvents.BACK_TO_MAP));
      });
    }

    // Start date change listeners
    this.setupDateFieldListeners('start', () => this.updateStartDate());

    // Current date change listeners
    this.setupDateFieldListeners('current', () => this.updateCurrentDate());

    // Quick advance buttons
    const advanceButtons = this.shadowRoot.querySelectorAll('.btn-advance');
    advanceButtons.forEach(btn => {
      this.addManagedListener(btn, 'click', () => {
        const days = parseInt(btn.dataset.days);
        this.advanceCurrentDate(days);
      });
    });

    // Bookmark modal listeners
    this.setupModalListeners('bookmark', {
      open: () => this.openBookmarkModal(),
      save: () => this.saveBookmark(),
      close: () => this.closeBookmarkModal()
    });

    // Journal modal listeners
    this.setupModalListeners('journal', {
      open: () => this.openJournalModal(),
      save: () => this.saveJournal(),
      close: () => this.closeJournalModal()
    });

    // Timeline search and filter listeners
    const searchInput = this.shadowRoot.getElementById('timeline-search');
    const filterSelect = this.shadowRoot.getElementById('timeline-filter');

    if (searchInput) {
      this.addManagedListener(searchInput, 'input', (e) => {
        this.timelineSearchQuery = e.target.value.toLowerCase();
        this.refreshTimeline();
      });
    }

    if (filterSelect) {
      this.addManagedListener(filterSelect, 'change', (e) => {
        this.timelineFilter = e.target.value;
        this.refreshTimeline();
      });
    }
  }

  /**
   * Helper method to set up date field change listeners
   * Reduces duplication for start/current date fields
   * @param {string} prefix - Field prefix (e.g., 'start', 'current')
   * @param {Function} updateCallback - Function to call when any field changes
   */
  setupDateFieldListeners(prefix, updateCallback) {
    const month = this.shadowRoot.getElementById(`${prefix}-month`);
    const day = this.shadowRoot.getElementById(`${prefix}-day`);
    const year = this.shadowRoot.getElementById(`${prefix}-year`);

    if (month) {
      this.addManagedListener(month, 'change', updateCallback);
    }

    if (day) {
      this.addManagedListener(day, 'change', updateCallback);
    }

    if (year) {
      this.addManagedListener(year, 'change', updateCallback);
    }
  }

  /**
   * Refresh just the timeline section without re-rendering the entire view
   * Used when search or filter changes
   */
  refreshTimeline() {
    const timelineContainer = this.shadowRoot.querySelector('.section:last-of-type');
    if (timelineContainer) {
      const timelineHTML = this.renderTimeline();
      const existingTimeline = timelineContainer.querySelector(`.timeline, p[style*="color: ${CONFIG.COLORS.TEXT_MUTED}"]`);

      // Create temp div and get the actual element (skip text nodes)
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = timelineHTML;
      const newTimeline = tempDiv.querySelector(`.timeline, p[style*="color: ${CONFIG.COLORS.TEXT_MUTED}"]`);

      if (!newTimeline) return; // Safety check

      if (existingTimeline) {
        existingTimeline.replaceWith(newTimeline);
      } else {
        // No existing timeline - append it to the container
        timelineContainer.appendChild(newTimeline);
      }
    }
  }

  /**
   * Helper method to set up modal event listeners
   * Reduces duplication for bookmark/journal modals
   * @param {string} prefix - Modal prefix (e.g., 'bookmark', 'journal')
   * @param {Object} callbacks - { open, save, close } callback functions
   */
  setupModalListeners(prefix, callbacks) {
    const addBtn = this.shadowRoot.getElementById(`add-${prefix}-btn`);
    const modal = this.shadowRoot.getElementById(`${prefix}-modal`);
    const saveBtn = this.shadowRoot.getElementById(`save-${prefix}-btn`);
    const cancelBtn = this.shadowRoot.getElementById(`cancel-${prefix}-btn`);

    if (addBtn) {
      this.addManagedListener(addBtn, 'click', callbacks.open);
    }

    if (saveBtn) {
      this.addManagedListener(saveBtn, 'click', callbacks.save);
    }

    if (cancelBtn) {
      this.addManagedListener(cancelBtn, 'click', callbacks.close);
    }

    // Close modal when clicking outside the form
    if (modal) {
      this.addManagedListener(modal, 'click', (e) => {
        if (e.target === modal) {
          callbacks.close();
        }
      });
    }
  }

  updateStartDate() {
    const month = parseInt(this.shadowRoot.getElementById('start-month').value);
    const day = parseInt(this.shadowRoot.getElementById('start-day').value);
    const year = parseInt(this.shadowRoot.getElementById('start-year').value);

    if (year < 1) return;

    // Validate day is valid for the selected month
    const maxDays = MONTH_LENGTHS[month - 1];
    if (day > maxDays) {
      toast.error(`Invalid date: ${MONTH_NAMES[month - 1]} only has ${maxDays} days. Please select a valid day.`);
      // Reset to current start date
      this.render();
      return;
    }

    const newStartDate = { year, month, day };

    // Update session
    this.session.calendar.campaignStartDate = newStartDate;

    // If current date is before new start date, update current date to match start date
    const comparison = calculateDaysElapsed(newStartDate, this.session.calendar.currentDate);
    if (comparison < 1) {
      this.session.calendar.currentDate = { ...newStartDate };
    }

    // Recalculate days elapsed
    this.session.calendar.daysElapsed = calculateDaysElapsed(
      this.session.calendar.campaignStartDate,
      this.session.calendar.currentDate
    );

    // Dispatch change event
    this.dispatchEvent(createHexEvent(HexEvents.CALENDAR_CHANGED, {
      session: this.session
    }));

    // Re-render to update display
    this.render();
  }

  updateCurrentDate() {
    const month = parseInt(this.shadowRoot.getElementById('current-month').value);
    const day = parseInt(this.shadowRoot.getElementById('current-day').value);
    const year = parseInt(this.shadowRoot.getElementById('current-year').value);

    if (year < 1) return;

    // Validate day is valid for the selected month
    const maxDays = MONTH_LENGTHS[month - 1];
    if (day > maxDays) {
      toast.error(`Invalid date: ${MONTH_NAMES[month - 1]} only has ${maxDays} days. Please select a valid day.`);
      // Reset to current date
      this.render();
      return;
    }

    const newCurrentDate = { year, month, day };

    // Validate current date is not before start date
    const daysElapsed = calculateDaysElapsed(this.session.calendar.campaignStartDate, newCurrentDate);
    if (daysElapsed < 1) {
      const startDateStr = formatGameDate(this.session.calendar.campaignStartDate);
      toast.error(`Invalid date: Current date cannot be before campaign start date (${startDateStr}).`);
      // Reset to current date
      this.render();
      return;
    }

    // Update session
    this.session.calendar.currentDate = newCurrentDate;

    // Recalculate days elapsed
    this.session.calendar.daysElapsed = calculateDaysElapsed(
      this.session.calendar.campaignStartDate,
      this.session.calendar.currentDate
    );

    // Dispatch change event
    this.dispatchEvent(createHexEvent(HexEvents.CALENDAR_CHANGED, {
      session: this.session
    }));

    // Re-render to update display
    this.render();
  }

  advanceCurrentDate(days) {
    const current = this.session.calendar.currentDate;
    const newDate = this.addDays(current, days);

    this.session.calendar.currentDate = newDate;
    this.session.calendar.daysElapsed = calculateDaysElapsed(
      this.session.calendar.campaignStartDate,
      this.session.calendar.currentDate
    );

    this.dispatchEvent(createHexEvent(HexEvents.CALENDAR_CHANGED, {
      session: this.session
    }));

    this.render();
  }

  addDays(date, daysToAdd) {
    let { year, month, day } = date;
    day += daysToAdd;

    while (day > MONTH_LENGTHS[month - 1]) {
      day -= MONTH_LENGTHS[month - 1];
      month++;
      if (month > 12) {
        month = 1;
        year++;
      }
    }

    return { year, month, day };
  }


  /**
   * Collect all campaign events for the timeline
   * @returns {Array} Array of event objects with date and description
   */
  collectTimelineEvents() {
    const events = [];
    const startDate = this.session.calendar?.campaignStartDate || DEFAULT_GAME_DATE;

    // Collect hex reveal events
    if (this.session.hexStates) {
      Object.entries(this.session.hexStates).forEach(([hexCoord, state]) => {
        if (state.revealHistory) {
          state.revealHistory.forEach(entry => {
            if (entry.gameDate) {
              events.push({
                type: entry.revealed ? 'hex_reveal' : 'hex_conceal',
                gameDate: entry.gameDate,
                hexCoord,
                description: entry.revealed ? `Hex ${hexCoord} revealed` : `Hex ${hexCoord} concealed`
              });
            }
          });
        }
      });
    }

    // Collect note events
    if (this.session.hexStates) {
      Object.entries(this.session.hexStates).forEach(([hexCoord, state]) => {
        if (state.notes) {
          state.notes.forEach(note => {
            if (note.gameDate) {
              const preview = note.content.length > 30
                ? note.content.substring(0, 30) + '...'
                : note.content;
              events.push({
                type: 'note',
                gameDate: note.gameDate,
                hexCoord,
                description: `Note added to hex ${hexCoord}: "${preview}"`
              });
            }
          });
        }
      });
    }

    // Collect icon events
    if (this.session.hexStates) {
      Object.entries(this.session.hexStates).forEach(([hexCoord, state]) => {
        if (state.icons) {
          state.icons.forEach(icon => {
            if (icon.gameDate) {
              events.push({
                type: 'icon',
                gameDate: icon.gameDate,
                hexCoord,
                iconName: icon.iconName,
                description: `Icon "${icon.iconName}" added to hex ${hexCoord}`
              });
            }
          });
        }
      });
    }

    // Collect token movement events
    if (this.session.tokens) {
      this.session.tokens.forEach(token => {
        if (token.positionHistory) {
          token.positionHistory.forEach((entry, index) => {
            if (entry.gameDate && entry.position) {
              const hexCoord = `${entry.position.col},${entry.position.row}`;
              const actionType = index === 0 ? 'placed' : 'moved';
              events.push({
                type: 'token_move',
                gameDate: entry.gameDate,
                hexCoord,
                tokenName: token.name,
                description: `Token "${token.name}" ${actionType} to hex ${hexCoord}`
              });
            }
          });
        }
      });
    }

    // Collect bookmark events
    if (this.session.calendar?.bookmarks) {
      this.session.calendar.bookmarks.forEach(bookmark => {
        if (bookmark.gameDate) {
          const description = bookmark.description
            ? `<span class="bookmark-title">${bookmark.title}</span> - ${bookmark.description}`
            : `<span class="bookmark-title">${bookmark.title}</span>`;
          events.push({
            type: 'bookmark',
            gameDate: bookmark.gameDate,
            title: bookmark.title,
            description: `📌 ${description}`
          });
        }
      });
    }

    // Collect journal entry events
    if (this.session.calendar?.journalEntries) {
      this.session.calendar.journalEntries.forEach(journal => {
        if (journal.gameDate) {
          events.push({
            type: 'journal',
            gameDate: journal.gameDate,
            description: `📝 ${journal.description}`
          });
        }
      });
    }

    // Sort events by date (oldest first)
    events.sort((a, b) => {
      const aDays = calculateDaysElapsed(startDate, a.gameDate);
      const bDays = calculateDaysElapsed(startDate, b.gameDate);
      return aDays - bDays;
    });

    return events;
  }

  /**
   * Filter timeline events based on search query and filter type
   * @param {Array} events - All timeline events
   * @returns {Array} Filtered events
   */
  filterTimelineEvents(events) {
    let filtered = events;

    // Apply category filter
    if (this.timelineFilter === 'bookmarks') {
      filtered = filtered.filter(e => e.type === 'bookmark');
    } else if (this.timelineFilter === 'journal') {
      filtered = filtered.filter(e => e.type === 'journal');
    }

    // Apply search filter
    if (this.timelineSearchQuery) {
      filtered = filtered.filter(event => {
        const searchableText = [
          event.description,
          event.title || '',
          event.hexCoord || '',
          event.tokenName || '',
          event.iconName || ''
        ].join(' ').toLowerCase();
        return searchableText.includes(this.timelineSearchQuery);
      });
    }

    return filtered;
  }

  /**
   * Group timeline events by date
   * @returns {Array} Array of date groups with events
   */
  groupEventsByDate() {
    const allEvents = this.collectTimelineEvents();
    const events = this.filterTimelineEvents(allEvents);
    const grouped = {};
    const startDate = this.session.calendar?.campaignStartDate || DEFAULT_GAME_DATE;

    events.forEach(event => {
      const dateKey = `${event.gameDate.year}-${event.gameDate.month}-${event.gameDate.day}`;
      if (!grouped[dateKey]) {
        const daysElapsed = calculateDaysElapsed(startDate, event.gameDate);
        grouped[dateKey] = {
          gameDate: event.gameDate,
          daysElapsed,
          dateFormatted: formatGameDateWithDays(event.gameDate, daysElapsed),
          events: []
        };
      }
      grouped[dateKey].events.push(event);
    });

    // Convert to array and sort by day
    return Object.values(grouped).sort((a, b) => a.daysElapsed - b.daysElapsed);
  }

  /**
   * Open the bookmark modal
   */
  openBookmarkModal() {
    this.openModal('bookmark', {
      clearFields: ['bookmark-title', 'bookmark-description'],
      focusField: 'bookmark-title'
    });
  }

  /**
   * Close the bookmark modal
   */
  closeBookmarkModal() {
    this.closeModal('bookmark-modal');
  }

  /**
   * Save a new bookmark
   */
  saveBookmark() {
    const titleInput = this.shadowRoot.getElementById('bookmark-title');
    const descriptionInput = this.shadowRoot.getElementById('bookmark-description');
    const monthSelect = this.shadowRoot.getElementById('bookmark-month');
    const daySelect = this.shadowRoot.getElementById('bookmark-day');
    const yearInput = this.shadowRoot.getElementById('bookmark-year');

    if (!titleInput || !monthSelect || !daySelect || !yearInput) return;

    const title = titleInput.value.trim();
    const description = descriptionInput ? descriptionInput.value.trim() : '';
    const month = parseInt(monthSelect.value);
    const day = parseInt(daySelect.value);
    const year = parseInt(yearInput.value);

    // Validate required fields
    if (!title) {
      showFieldError(titleInput, 'Please enter a title for the bookmark.');
      clearErrorOnInput(titleInput);
      return;
    }

    if (year < 1) {
      showFieldError(yearInput, 'Please enter a valid year (1 or greater).');
      clearErrorOnInput(yearInput);
      return;
    }

    // Initialize bookmarks array if it doesn't exist
    if (!this.session.calendar.bookmarks) {
      this.session.calendar.bookmarks = [];
    }

    // Create bookmark object
    const bookmark = {
      title,
      description,
      gameDate: { year, month, day }
    };

    // Add to bookmarks array
    this.session.calendar.bookmarks.push(bookmark);

    // Mark session as modified
    if (window.currentSession) {
      window.currentSession.isModified = true;
    }

    // Close modal
    this.closeBookmarkModal();

    // Re-render to show the new bookmark
    this.render();

    // Dispatch calendar-changed event
    this.dispatchEvent(createHexEvent(HexEvents.CALENDAR_CHANGED));
  }

  /**
   * Open the journal entry modal
   */
  openJournalModal() {
    this.openModal('journal', {
      clearFields: ['journal-description'],
      focusField: 'journal-description'
    });
  }

  /**
   * Close the journal entry modal
   */
  closeJournalModal() {
    this.closeModal('journal-modal');
  }

  /**
   * Save a new journal entry
   */
  saveJournal() {
    const descriptionInput = this.shadowRoot.getElementById('journal-description');
    const monthSelect = this.shadowRoot.getElementById('journal-month');
    const daySelect = this.shadowRoot.getElementById('journal-day');
    const yearInput = this.shadowRoot.getElementById('journal-year');

    if (!descriptionInput || !monthSelect || !daySelect || !yearInput) return;

    const description = descriptionInput.value.trim();
    const month = parseInt(monthSelect.value);
    const day = parseInt(daySelect.value);
    const year = parseInt(yearInput.value);

    // Validate required fields
    if (!description) {
      showFieldError(descriptionInput, 'Please enter a description for the journal entry.');
      clearErrorOnInput(descriptionInput);
      return;
    }

    if (year < 1) {
      showFieldError(yearInput, 'Please enter a valid year (1 or greater).');
      clearErrorOnInput(yearInput);
      return;
    }

    // Initialize journal entries array if it doesn't exist
    if (!this.session.calendar.journalEntries) {
      this.session.calendar.journalEntries = [];
    }

    // Create journal entry object (no title field)
    const journalEntry = {
      description,
      gameDate: { year, month, day }
    };

    // Add to journal entries array
    this.session.calendar.journalEntries.push(journalEntry);

    // Mark session as modified
    if (window.currentSession) {
      window.currentSession.isModified = true;
    }

    // Close modal
    this.closeJournalModal();

    // Re-render to show the new journal entry
    this.render();

    // Dispatch calendar-changed event
    this.dispatchEvent(createHexEvent(HexEvents.CALENDAR_CHANGED));
  }

  /**
   * Generic modal opener - DRY helper for bookmark/journal modals
   * @param {string} prefix - Modal prefix ('bookmark' or 'journal')
   * @param {Object} config - Configuration object
   * @param {Array} config.clearFields - IDs of fields to clear
   * @param {string} config.focusField - ID of field to focus
   */
  openModal(prefix, config) {
    const modal = this.shadowRoot.getElementById(`${prefix}-modal`);
    if (!modal) return;

    // Set default date to current campaign date
    const currentDate = this.session.calendar.currentDate;
    const monthSelect = this.shadowRoot.getElementById(`${prefix}-month`);
    const daySelect = this.shadowRoot.getElementById(`${prefix}-day`);
    const yearInput = this.shadowRoot.getElementById(`${prefix}-year`);

    if (monthSelect) monthSelect.value = currentDate.month;
    if (daySelect) {
      daySelect.innerHTML = this.getDayOptions(currentDate.month, currentDate.day);
    }
    if (yearInput) yearInput.value = currentDate.year;

    // Clear specified form fields
    if (config.clearFields) {
      config.clearFields.forEach(fieldId => {
        const field = this.shadowRoot.getElementById(fieldId);
        if (field) field.value = '';
      });
    }

    modal.classList.remove('hidden');

    // Focus on specified field
    if (config.focusField) {
      const focusField = this.shadowRoot.getElementById(config.focusField);
      if (focusField) {
        setTimeout(() => focusField.focus(), CONFIG.FOCUS_DELAY);
      }
    }
  }

  /**
   * Generic modal closer - DRY helper for all modals
   * @param {string} modalId - ID of modal to close
   */
  closeModal(modalId) {
    const modal = this.shadowRoot.getElementById(modalId);
    if (modal) {
      modal.classList.add('hidden');
    }
  }
}

customElements.define('calendar-view', CalendarView);

export default CalendarView;
